import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import LeadModal from '@/components/LeadModal';
import {
  Search, Tag, Trash2, Plus, RefreshCw, CheckSquare,
  ChevronDown, X, Workflow, Filter, Users, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

import { API_URL } from '@/config/api';

const STATUS_COLORS = {
  new: 'bg-blue-100 text-blue-700',
  interested: 'bg-green-100 text-green-700',
  not_interested: 'bg-red-100 text-red-700',
  callback: 'bg-amber-100 text-amber-700',
  contacted: 'bg-purple-100 text-purple-700',
  qualified: 'bg-teal-100 text-teal-700',
  won: 'bg-emerald-100 text-emerald-700',
  lost: 'bg-slate-100 text-slate-600',
};

const LeadsPage = () => {
  const { user, selectedBusiness, selectedCampaign } = useAuth();
  const businessId = selectedCampaign?.business_id || selectedBusiness?.business_id || user?.business_id;

  // Data
  const [leads, setLeads] = useState([]);
  const [tags, setTags] = useState([]);
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);

  // Filters
  const [search, setSearch] = useState('');
  const [filterTagId, setFilterTagId] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 50;

  // Selection
  const [selectedIds, setSelectedIds] = useState(new Set());
  const allSelected = leads.length > 0 && leads.every(l => selectedIds.has(l.lead_id));
  const someSelected = selectedIds.size > 0;

  // Modals
  const [selectedLead, setSelectedLead] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showBulkTagModal, setShowBulkTagModal] = useState(false);
  const [showBulkWorkflowModal, setShowBulkWorkflowModal] = useState(false);
  const [bulkTagId, setBulkTagId] = useState('');
  const [bulkWorkflowId, setBulkWorkflowId] = useState('');
  const [bulkLoading, setBulkLoading] = useState(false);

  const authHeaders = () => {
    const token = localStorage.getItem('token');
    return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
  };

  const fetchLeads = useCallback(async () => {
    if (!businessId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        business_id: businessId,
        skip: page * PAGE_SIZE,
        limit: PAGE_SIZE,
      });
      if (selectedCampaign?.campaign_id) params.set('campaign_id', selectedCampaign.campaign_id);
      if (filterTagId && filterTagId !== 'all') params.set('tag_id', filterTagId);
      if (filterStatus && filterStatus !== 'all') params.set('status', filterStatus);
      if (search) params.set('search', search);

      const res = await fetch(`${API_URL}/api/leads?${params}`, {
        credentials: 'include', headers: authHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setLeads(data);
        setTotal(data.length < PAGE_SIZE ? page * PAGE_SIZE + data.length : (page + 1) * PAGE_SIZE + 1);
      }
    } finally {
      setLoading(false);
    }
  }, [businessId, selectedCampaign, filterTagId, filterStatus, search, page]);

  const fetchTags = useCallback(async () => {
    if (!businessId) return;
    const res = await fetch(`${API_URL}/api/tags?business_id=${businessId}`, {
      credentials: 'include', headers: authHeaders(),
    });
    if (res.ok) setTags(await res.json());
  }, [businessId]);

  const fetchWorkflows = useCallback(async () => {
    if (!businessId) return;
    const res = await fetch(`${API_URL}/api/workflows?business_id=${businessId}`, {
      credentials: 'include', headers: authHeaders(),
    });
    if (res.ok) setWorkflows(await res.json());
  }, [businessId]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);
  useEffect(() => { fetchTags(); fetchWorkflows(); }, [fetchTags, fetchWorkflows]);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => { setPage(0); fetchLeads(); }, 400);
    return () => clearTimeout(t);
  }, [search]);

  // ── Selection helpers ──
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(leads.map(l => l.lead_id)));
    }
  };

  const clearSelection = () => setSelectedIds(new Set());

  // ── Single delete ──
  const deleteSingleLead = async (leadId) => {
    const res = await fetch(`${API_URL}/api/leads/${leadId}`, {
      method: 'DELETE', credentials: 'include', headers: authHeaders(),
    });
    if (res.ok) {
      toast.success('Lead deleted');
      setLeads(prev => prev.filter(l => l.lead_id !== leadId));
      selectedIds.delete(leadId);
      setSelectedIds(new Set(selectedIds));
    } else {
      toast.error('Failed to delete lead');
    }
  };

  // ── Bulk delete ──
  const handleBulkDelete = async () => {
    setBulkLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/leads/bulk-delete`, {
        method: 'POST',
        credentials: 'include',
        headers: authHeaders(),
        body: JSON.stringify({ lead_ids: [...selectedIds] }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`Deleted ${data.deleted} lead${data.deleted !== 1 ? 's' : ''}`);
        setShowDeleteConfirm(false);
        clearSelection();
        fetchLeads();
      } else {
        toast.error(data.detail || 'Bulk delete failed');
      }
    } finally {
      setBulkLoading(false);
    }
  };

  // ── Bulk tag ──
  const handleBulkTag = async () => {
    if (!bulkTagId) { toast.error('Select a tag'); return; }
    setBulkLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/leads/bulk-tag`, {
        method: 'POST',
        credentials: 'include',
        headers: authHeaders(),
        body: JSON.stringify({ lead_ids: [...selectedIds], tag_id: bulkTagId }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`Tagged ${data.tagged} lead${data.tagged !== 1 ? 's' : ''} with "${data.tag_name}"`);
        setShowBulkTagModal(false);
        setBulkTagId('');
        clearSelection();
        fetchLeads();
      } else {
        toast.error(data.detail || 'Tagging failed');
      }
    } finally {
      setBulkLoading(false);
    }
  };

  // ── Bulk workflow ──
  const handleBulkWorkflow = async () => {
    if (!bulkWorkflowId) { toast.error('Select a workflow'); return; }
    setBulkLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/leads/bulk-workflow`, {
        method: 'POST',
        credentials: 'include',
        headers: authHeaders(),
        body: JSON.stringify({ lead_ids: [...selectedIds], workflow_id: bulkWorkflowId }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`Enrolled ${data.enrolled} lead${data.enrolled !== 1 ? 's' : ''} in "${data.workflow_name}"`);
        setShowBulkWorkflowModal(false);
        setBulkWorkflowId('');
        clearSelection();
      } else {
        toast.error(data.detail || 'Enrollment failed');
      }
    } finally {
      setBulkLoading(false);
    }
  };

  const tagMap = Object.fromEntries(tags.map(t => [t.tag_id, t]));

  // ── Lead tags: fetch for all displayed leads in one bulk call ──
  const [leadTags, setLeadTags] = useState({});
  useEffect(() => {
    if (!leads.length) return;
    const fetchBulkTags = async () => {
      try {
        const leadIds = leads.map(l => l.lead_id);
        const res = await fetch(`${API_URL}/api/leads/bulk-tags`, {
          method: 'POST',
          credentials: 'include',
          headers: { ...authHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ lead_ids: leadIds }),
        });
        if (res.ok) setLeadTags(await res.json());
      } catch (err) {
        console.error('Bulk tags fetch error:', err);
      }
    };
    fetchBulkTags();
  }, [leads]);

  if (!businessId) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <div className="text-center">
          <Users className="mx-auto mb-3 h-12 w-12 text-slate-300" />
          <p className="text-slate-500">Select a business to view leads</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Leads</h1>
          <p className="text-sm text-slate-500">
            {selectedCampaign ? `Campaign: ${selectedCampaign.name}` : 'All campaigns'}
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={fetchLeads} disabled={loading}>
          <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search name, email, phone…"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(0); }}
            className="pl-9"
          />
        </div>
        <Select value={filterTagId || 'all'} onValueChange={v => { setFilterTagId(v === 'all' ? '' : v); setPage(0); }}>
          <SelectTrigger className="w-44">
            <Tag className="mr-1.5 h-3.5 w-3.5 text-slate-400" />
            <SelectValue placeholder="Filter by tag" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All tags</SelectItem>
            {tags.map(t => (
              <SelectItem key={t.tag_id} value={t.tag_id}>
                <span className="flex items-center gap-2">
                  <span className="inline-block h-2 w-2 rounded-full" style={{ background: t.color }} />
                  {t.name}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterStatus || 'all'} onValueChange={v => { setFilterStatus(v === 'all' ? '' : v); setPage(0); }}>
          <SelectTrigger className="w-40">
            <Filter className="mr-1.5 h-3.5 w-3.5 text-slate-400" />
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {['new','interested','not_interested','callback','contacted','qualified','won','lost'].map(s => (
              <SelectItem key={s} value={s}>{s.replace('_', ' ')}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {(filterTagId || filterStatus || search) && (
          <Button variant="ghost" size="sm" onClick={() => { setSearch(''); setFilterTagId(''); setFilterStatus(''); setPage(0); }}>
            <X className="mr-1 h-3.5 w-3.5" /> Clear
          </Button>
        )}
      </div>

      {/* Bulk action bar */}
      {someSelected && (
        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3">
          <span className="text-sm font-semibold text-blue-700">
            {selectedIds.size} selected
          </span>
          <div className="ml-auto flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => setShowBulkTagModal(true)}>
              <Tag className="mr-1.5 h-3.5 w-3.5" />
              Add Tag
            </Button>
            <Button size="sm" variant="outline" onClick={() => setShowBulkWorkflowModal(true)}>
              <Workflow className="mr-1.5 h-3.5 w-3.5" />
              Add to Workflow
            </Button>
            <Button size="sm" variant="destructive" onClick={() => setShowDeleteConfirm(true)}>
              <Trash2 className="mr-1.5 h-3.5 w-3.5" />
              Delete
            </Button>
            <Button size="sm" variant="ghost" onClick={clearSelection}>
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        {/* Table header */}
        <div className="grid grid-cols-[40px_1fr_160px_100px_120px_160px_80px] items-center gap-3 border-b border-slate-100 bg-slate-50 px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
          <input
            type="checkbox"
            checked={allSelected}
            onChange={toggleSelectAll}
            className="h-4 w-4 rounded accent-blue-600 cursor-pointer"
          />
          <span>Lead</span>
          <span>Contact</span>
          <span>Date</span>
          <span>Status</span>
          <span>Tags</span>
          <span>Actions</span>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-400">
            <RefreshCw className="mr-2 h-5 w-5 animate-spin" />
            Loading leads…
          </div>
        ) : leads.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-slate-400">
            <Users className="mb-3 h-10 w-10 text-slate-200" />
            <p className="font-medium">No leads found</p>
            <p className="text-sm">{search || filterTagId || filterStatus ? 'Try adjusting your filters' : 'Upload leads to get started'}</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {leads.map(lead => {
              const isSelected = selectedIds.has(lead.lead_id);
              const tags = leadTags[lead.lead_id] || [];
              return (
                <div
                  key={lead.lead_id}
                  className={`grid grid-cols-[40px_1fr_160px_100px_120px_160px_80px] items-center gap-3 px-4 py-3 text-sm transition-colors ${isSelected ? 'bg-blue-50' : 'hover:bg-slate-50'}`}
                >
                  {/* Checkbox */}
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleSelect(lead.lead_id)}
                    className="h-4 w-4 rounded accent-blue-600 cursor-pointer"
                    onClick={e => e.stopPropagation()}
                  />

                  {/* Name + source */}
                  <button
                    type="button"
                    className="flex flex-col text-left"
                    onClick={() => setSelectedLead(lead)}
                  >
                    <span className="font-semibold text-slate-900 hover:text-blue-600 truncate">{lead.name}</span>
                    <span className="text-xs text-slate-400 capitalize">{lead.source}</span>
                  </button>

                  {/* Contact */}
                  <div className="flex flex-col text-xs text-slate-500 truncate">
                    {lead.email && <span className="truncate">{lead.email}</span>}
                    {lead.phone && <span>{lead.phone}</span>}
                    {!lead.email && !lead.phone && <span className="text-slate-300">—</span>}
                  </div>

                  {/* Date */}
                  <div className="text-xs text-slate-500">
                    {lead.created_at ? new Date(lead.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                  </div>

                  {/* Status */}
                  <Badge className={`${STATUS_COLORS[lead.status] || STATUS_COLORS.new} capitalize text-xs border-0`}>
                    {lead.status?.replace('_', ' ')}
                  </Badge>

                  {/* Tags */}
                  <div className="flex flex-wrap gap-1">
                    {tags.slice(0, 2).map(t => (
                      <span
                        key={t.tag_id}
                        className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium"
                        style={{ background: t.color + '22', color: t.color }}
                      >
                        <span className="inline-block h-1.5 w-1.5 rounded-full" style={{ background: t.color }} />
                        {t.name}
                      </span>
                    ))}
                    {tags.length > 2 && (
                      <span className="text-[10px] text-slate-400">+{tags.length - 2}</span>
                    )}
                  </div>

                  {/* Actions */}
                  <button
                    type="button"
                    onClick={() => deleteSingleLead(lead.lead_id)}
                    className="ml-auto rounded-lg p-1.5 text-slate-400 transition hover:bg-red-50 hover:text-red-500"
                    title="Delete lead"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {leads.length === PAGE_SIZE && (
          <div className="flex items-center justify-between border-t border-slate-100 px-4 py-3 text-sm text-slate-500">
            <span>Showing {page * PAGE_SIZE + 1}–{page * PAGE_SIZE + leads.length}</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}>Prev</Button>
              <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)}>Next</Button>
            </div>
          </div>
        )}
      </div>

      {/* Lead detail modal */}
      {selectedLead && (
        <LeadModal
          isOpen={!!selectedLead}
          lead={selectedLead}
          onClose={() => { setSelectedLead(null); fetchLeads(); }}
          onUpdate={(updated) => {
            if (updated && updated.lead_id) {
              setLeads(prev => prev.map(l => l.lead_id === updated.lead_id ? updated : l));
              setSelectedLead(updated);
            } else {
              fetchLeads();
            }
          }}
        />
      )}

      {/* Bulk delete confirm */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Delete {selectedIds.size} Lead{selectedIds.size !== 1 ? 's' : ''}?
            </DialogTitle>
          </DialogHeader>
          <Alert className="border-red-200 bg-red-50">
            <AlertDescription className="text-red-700">
              This cannot be undone. All selected leads and their tags will be permanently deleted.
            </AlertDescription>
          </Alert>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleBulkDelete} disabled={bulkLoading}>
              {bulkLoading ? 'Deleting…' : `Delete ${selectedIds.size} lead${selectedIds.size !== 1 ? 's' : ''}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk tag modal */}
      <Dialog open={showBulkTagModal} onOpenChange={setShowBulkTagModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Tag className="h-5 w-5 text-blue-500" />
              Add Tag to {selectedIds.size} Lead{selectedIds.size !== 1 ? 's' : ''}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-slate-500">Select a tag to apply to all selected leads. Leads already tagged will be skipped.</p>
            {tags.length === 0 ? (
              <Alert><AlertDescription>No tags found. Create tags in the Admin panel first.</AlertDescription></Alert>
            ) : (
              <Select value={bulkTagId} onValueChange={setBulkTagId}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a tag…" />
                </SelectTrigger>
                <SelectContent>
                  {tags.map(t => (
                    <SelectItem key={t.tag_id} value={t.tag_id}>
                      <span className="flex items-center gap-2">
                        <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: t.color }} />
                        {t.name}
                        <span className="text-xs text-slate-400">({t.lead_count} leads)</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {bulkTagId && workflows.length > 0 && (
              <div className="rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-700">
                <strong>Tip:</strong> After tagging, use "Add to Workflow" to enroll these leads in a workflow triggered by this tag.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowBulkTagModal(false); setBulkTagId(''); }}>Cancel</Button>
            <Button onClick={handleBulkTag} disabled={!bulkTagId || bulkLoading}>
              {bulkLoading ? 'Tagging…' : 'Apply Tag'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk workflow modal */}
      <Dialog open={showBulkWorkflowModal} onOpenChange={setShowBulkWorkflowModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Workflow className="h-5 w-5 text-purple-500" />
              Add {selectedIds.size} Lead{selectedIds.size !== 1 ? 's' : ''} to Workflow
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-slate-500">Select a workflow to enroll all selected leads. Already-enrolled leads will be skipped.</p>
            {workflows.length === 0 ? (
              <Alert><AlertDescription>No workflows found. Create a workflow first.</AlertDescription></Alert>
            ) : (
              <Select value={bulkWorkflowId} onValueChange={setBulkWorkflowId}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a workflow…" />
                </SelectTrigger>
                <SelectContent>
                  {workflows.map(w => (
                    <SelectItem key={w.workflow_id} value={w.workflow_id}>
                      <span className="flex items-center gap-2">
                        {w.name}
                        <Badge variant="secondary" className="text-[10px]">{w.trigger_type?.replace('_', ' ')}</Badge>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowBulkWorkflowModal(false); setBulkWorkflowId(''); }}>Cancel</Button>
            <Button onClick={handleBulkWorkflow} disabled={!bulkWorkflowId || bulkLoading}>
              {bulkLoading ? 'Enrolling…' : 'Add to Workflow'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LeadsPage;
